<template>
	<v-card
		min-height="5em"
		min-width="20em"
		max-width="50em"
	>
		<v-card-text class="sgnl-py-4 sgnl-px-0">
			<v-table density="compact">
				<template v-slot:default>
					<thead>
						<tr>
							<th class="sgnl-text-left" />
							<th class="sgnl-text-left">Active Core</th>
							<th
								v-if="minimumCoreConfig"
								class="sgnl-text-left"
							>
								Required Core
							</th>
						</tr>
					</thead>
					<tbody>
						<template v-for="key in fieldsToDisplay">
							<tr
								v-if="Object.hasOwnProperty.call(activeCore, key)"
								:key="key"
								:class="coreRequirementWarnings[key] ? 'sgnl-text-color-error' : ''"
							>
								<td>{{ formatKeyString(key) }}</td>
								<td>{{ formatValueString(key, activeCore[key]) }}</td>
								<td
									v-if="minimumCoreConfig"
									class="sgnl-text-left"
								>
									<span
										v-if="limitValuePrefix(key)"
										class="sgnl-caption-regular"
										:class="coreRequirementWarnings[key] ? 'sgnl-text-color-error' : ''"
										>≥</span
									>
									{{ formatValueString(key, minimumCoreConfig[key]) ?? "-" }}
								</td>
							</tr>
						</template>
					</tbody>
				</template>
			</v-table>
		</v-card-text>
	</v-card>
</template>

<script lang="ts">
import { toStringWithByteMagnitude, toStringWithSIMagnitude } from "@/js/utilities";
import { useCoresStore } from "@/stores/cores";
import { Core, CoreSpecs, CoreRequirementMatch } from "@/types/api/cores";
import { mapState } from "pinia";
import { defineComponent, PropType } from "vue";

// Types

// Libraries

// Utilities

// Components

// Stores

// Global variables

export default defineComponent({
	name: "ActiveCoreTooltip",
	props: {
		activeCore: {
			type: Object as PropType<CoreSpecs>,
			default: null,
		},
		minimumCoreConfig: {
			type: Object as PropType<Partial<Core>>,
		},
	},
	setup() {
		const fieldsToDisplay = [
			"CoreID",
			"Name",
			"Class",
			"Precision",
			"MemorySize",
			"Microarchitecture",
			"CorrelationTracking",
		];
		return { fieldsToDisplay };
	},
	computed: {
		...mapState(useCoresStore, {
			coresSatisfyMinConfig: "coresSatisfyMinConfig",
			correlationTrackingMethodByCode: "correlationTrackingMethodByCode",
		}),
		coreRequirementWarnings(): Partial<CoreRequirementMatch> {
			const requirementMatch = {};
			for (const key in this.minimumCoreConfig) {
				if (typeof this.minimumCoreConfig[key] == "number") {
					// check if requirement satisfied for numerical constraints
					requirementMatch[key] = this.activeCore[key] < this.minimumCoreConfig[key];
				} else {
					// check if requirement satisfied for non numerical constraints
					if (["Class", "Microarchitecture", "CorrelationTracking"].includes(key)) {
						requirementMatch[key] = this.activeCore[key] !== this.minimumCoreConfig[key];
					}
				}
			}
			return requirementMatch;
		},
		allRequirementsMatched() {
			for (const key in this.coreRequirementWarnings) {
				if (Object.prototype.hasOwnProperty.call(this.coreRequirementWarnings, key)) {
					if (this.coreRequirementWarnings[key]) {
						return false;
					}
				}
			}
			return true;
		},
	},
	methods: {
		limitValuePrefix(key) {
			switch (key) {
				case "MemorySize":
				case "Precision":
					return true;
				default:
					return false;
			}
		},
		formatValueString(key, value) {
			switch (key) {
				case "MemorySize":
					return toStringWithByteMagnitude(value);
				case "Precision":
					return toStringWithSIMagnitude(value, "");
				case "CorrelationTracking":
					return this.formatCorrelationString(value);
				default:
					return value;
			}
		},
		formatKeyString(key) {
			switch (key) {
				case "CoreID":
					return "Core ID";
				case "MemorySize":
					return "Memory Size";
				case "CorrelationTracking":
					return "Correlation Tracking";
				default:
					return key;
			}
		},
		formatCorrelationString(code) {
			return this.correlationTrackingMethodByCode(code)?.Name ?? "-";
		},
	},
});
</script>
